import type { ReactElement } from 'react';
import { createBrowserRouter, type RouteObject } from 'react-router';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { EmployeeDashboardPage } from '@/pages/employees/EmployeeDashboardPage';
import { EmployeesPage } from '@/pages/employees/EmployeesPage';
import { ForgotPasswordPage } from '@/pages/ForgotPasswordPage';
import { HistoryDetailPage } from '@/pages/history/HistoryDetailPage';
import { HistorySheetPage } from '@/pages/history/HistorySheetPage';
import { HistoryPage } from '@/pages/history/HistoryPage';
import { RequestDetailPage } from '@/pages/requests/RequestDetailPage';
import { RequestsPage } from '@/pages/requests/RequestsPage';
import { ExportsPage } from '@/pages/exports/ExportsPage';
import { DashboardPage } from '@/pages/dashboard/DashboardPage';
import { TimesheetPage } from '@/pages/timesheet/TimesheetPage';
import { ValidationDetailPage } from '@/pages/validation/ValidationDetailPage';
import { ValidationPage } from '@/pages/validation/ValidationPage';
import { ErrorPage } from '@/pages/ErrorPage';
import { LoginPage } from '@/pages/LoginPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { DefinePasswordPage } from '@/pages/DefinePasswordPage';
import { PasswordPage } from '@/pages/PasswordPage';
import { PlaceholderPage } from '@/pages/PlaceholderPage';
import { ProfilePage } from '@/pages/ProfilePage';
import { ResetPasswordPage } from '@/pages/ResetPasswordPage';
import { SsoCallbackPage } from '@/pages/SsoCallbackPage';
import { TeamDashboardPage } from '@/pages/teams/TeamDashboardPage';
import { TeamsPage } from '@/pages/teams/TeamsPage';
import { UserDashboardPage } from '@/pages/users/UserDashboardPage';
import { UsersPage } from '@/pages/users/UsersPage';
import { HomeRedirect, RequireAccess, RequireAuth } from '@/security/RequireAuth';
import { DEFINE_PASSWORD_PATH, NAV_ITEMS, PASSWORD_PATH, PROFILE_PATH } from '@/security/navigation';

/** Écrans déjà implémentés, par chemin de navigation (les autres affichent PlaceholderPage) */
const SCREENS: Record<string, ReactElement> = {
  '/dashboard': <DashboardPage />,
  '/employees': <EmployeesPage />,
  '/exports': <ExportsPage />,
  '/history': <HistoryPage />,
  '/timesheet': <TimesheetPage />,
  '/requests': <RequestsPage />,
  '/validation': <ValidationPage />,
  '/teams': <TeamsPage />,
  '/users': <UsersPage />,
};

export const routes: RouteObject[] = [
  {
    // Erreur de rendu ou de chargement d'un écran : ErrorPage (signalée à Sentry) plutôt que l'écran de React Router
    errorElement: <ErrorPage />,
    children: [
      { path: '/login', element: <LoginPage /> },
      { path: '/forgot-password', element: <ForgotPasswordPage /> },
      { path: '/reset-password', element: <ResetPasswordPage /> },
      // Retour de la connexion Okta (src/security/sso.ts)
      { path: '/sso/callback', element: <SsoCallbackPage /> },
      {
        element: <RequireAuth />,
        children: [
          // Étape de connexion (mot de passe local imposé), hors de l'application : ni sidebar ni menu
          { path: DEFINE_PASSWORD_PATH, element: <DefinePasswordPage /> },
          {
            element: <AdminLayout />,
            children: [
              { index: true, element: <HomeRedirect /> },
              { path: PASSWORD_PATH, element: <PasswordPage /> },
              { path: PROFILE_PATH, element: <ProfilePage /> },
              {
                element: <RequireAccess />,
                // TODO : remplacer chaque PlaceholderPage par l'écran correspondant du prototype
                children: [
                  ...NAV_ITEMS.filter((item) => item.section !== 'account').map((item) => ({
                    path: item.path,
                    element: SCREENS[item.path] ?? <PlaceholderPage />,
                  })),
                  // Sous-pages : mêmes droits que leur page (canAccess)
                  { path: '/users/:id', element: <UserDashboardPage /> },
                  { path: '/employees/:id', element: <EmployeeDashboardPage /> },
                  { path: '/teams/:id', element: <TeamDashboardPage /> },
                  { path: '/history/:id', element: <HistoryDetailPage /> },
                  { path: '/history/:id/sheets/:sheetId', element: <HistorySheetPage /> },
                  { path: '/requests/:id', element: <RequestDetailPage /> },
                  { path: '/validation/:id', element: <ValidationDetailPage /> },
                ],
              },
            ],
          },
        ],
      },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
];

export const router = createBrowserRouter(routes);
